import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Button, Alert, Row, Col } from 'react-bootstrap';
import api from '../api/axios';
import AuthContext from '../context/AuthContext';
import SiteFooter from '../components/SiteFooter';
import TermsAgreement from '../components/TermsAgreement';

// 공개 가입 페이지 (2026-04-28~):
// - 사전예약/초대코드 의존성 없음. 누구나 접근 가능.
// - 네이버로 시작 (간편 가입) 또는 일반 가입 form (ID/PW)
// - 초대코드는 옵셔널 (URL ?code=XXX 또는 form 안의 입력란)
// - Cloudflare Turnstile 봇 검증 필수

const NAVER_OAUTH_START = `${process.env.REACT_APP_API_URL || ''}/oauth/naver`;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const SignupPage = () => {
    const [searchParams] = useSearchParams();
    const initialInviteCode = searchParams.get('code') || '';
    const navigate = useNavigate();
    const { loginWithToken } = useContext(AuthContext);

    const [stage, setStage] = useState('form');   // 'form' | 'submitting' | 'done'
    const [error, setError] = useState('');

    // 약관 동의 (둘 다 필수) — 미체크 시 가입 버튼 비활성
    const [agreedTerms, setAgreedTerms] = useState(false);
    const [agreedPrivacy, setAgreedPrivacy] = useState(false);
    const agreedAll = agreedTerms && agreedPrivacy;

    // 폼 상태 (사용자가 모든 필드 직접 입력)
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [ownerName, setOwnerName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [shopName, setShopName] = useState('');
    const [businessNumber, setBusinessNumber] = useState('');
    // 운영 형태: 현재 예약제만 지원 (현장제는 추후 요금제 분리 시 추가)
    const businessType = 'reservation';
    const [bayCount, setBayCount] = useState(1);
    const [inviteCode, setInviteCode] = useState(initialInviteCode);

    // Turnstile
    const turnstileRef = useRef(null);
    const [turnstileToken, setTurnstileToken] = useState('');
    const [turnstileWidgetId, setTurnstileWidgetId] = useState(null);

    // Cloudflare Turnstile 스크립트 로드 + 위젯 렌더
    useEffect(() => {
        if (!TURNSTILE_SITE_KEY) return;   // dev 환경 등 site key 없으면 skip
        const SCRIPT_ID = 'cf-turnstile-script';
        const renderWidget = () => {
            if (!window.turnstile || !turnstileRef.current) return;
            const id = window.turnstile.render(turnstileRef.current, {
                sitekey: TURNSTILE_SITE_KEY,
                callback: (token) => setTurnstileToken(token),
                'expired-callback': () => setTurnstileToken(''),
                'error-callback': () => setTurnstileToken(''),
            });
            setTurnstileWidgetId(id);
        };
        if (window.turnstile) {
            renderWidget();
            return;
        }
        if (document.getElementById(SCRIPT_ID)) return;
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.onload = renderWidget;
        document.body.appendChild(script);
        return () => {
            if (turnstileWidgetId !== null && window.turnstile) {
                try { window.turnstile.remove(turnstileWidgetId); } catch {}
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const requireTurnstile = !!TURNSTILE_SITE_KEY;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== passwordConfirm) {
            setError('비밀번호가 일치하지 않습니다.');
            return;
        }
        if (password.length < 8) {
            setError('비밀번호는 8자 이상이어야 합니다.');
            return;
        }
        if (bayCount < 1 || bayCount > 4) {
            setError('베이 수는 1~4 사이여야 합니다.');
            return;
        }
        if (requireTurnstile && !turnstileToken) {
            setError('봇 검증을 완료해주세요.');
            return;
        }

        setStage('submitting');
        try {
            const res = await api.post('/signup', {
                username,
                password,
                owner_name: ownerName,
                email,
                phone,
                shopName,
                business_number: businessNumber,
                business_type: businessType,
                use_reservation: businessType === 'reservation',
                bay_count: Number(bayCount),
                invite_code: inviteCode || undefined,
                turnstileToken,
            });
            const { accessToken } = res.data.data;
            loginWithToken(accessToken);
            setStage('done');
            setTimeout(() => navigate('/', { replace: true }), 1000);
        } catch (err) {
            const msg = err.response?.data?.errors?.[0]?.msg || '가입에 실패했습니다.';
            setError(msg);
            setStage('form');
            // Turnstile token은 1회용 → 재발급 요청
            if (window.turnstile && turnstileWidgetId !== null) {
                try { window.turnstile.reset(turnstileWidgetId); } catch {}
            }
            setTurnstileToken('');
        }
    };

    const handleNaverSignup = () => {
        const params = new URLSearchParams({ mode: 'signup' });
        if (inviteCode) params.set('invite_code', inviteCode);
        window.location.href = `${NAVER_OAUTH_START}?${params.toString()}`;
    };

    if (stage === 'done') {
        return (
            <div className="login-page">
                <div className="login-form-panel" style={{ width: '100%' }}>
                    <div className="login-form-wrapper">
                        <h2>가입이 완료되었습니다!</h2>
                        <p>30일 무료 체험을 시작합니다. 잠시 후 워크보드로 이동합니다.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-page">
            <div className="login-brand-panel">
                <div className="login-brand-content">
                    <div className="login-brand-logo">W</div>
                    <h1 className="login-brand-title">WashNote에 오신 것을 환영합니다</h1>
                    <p className="login-brand-subtitle">30일 무료 체험을 시작하세요</p>
                </div>
                <div className="login-brand-footer">&copy; {new Date().getFullYear()} WashNote</div>
            </div>

            <div className="login-form-panel">
                <div className="login-form-wrapper">
                    <div className="login-form-header">
                        <h2 className="login-form-title">계정 만들기</h2>
                        <p className="login-form-desc">매장 정보를 입력해주세요</p>
                    </div>

                    <Button
                        variant="success"
                        className="login-submit"
                        style={{ marginBottom: 16, background: '#03C75A', borderColor: '#03C75A' }}
                        onClick={handleNaverSignup}
                        disabled={stage === 'submitting'}
                    >
                        네이버로 시작하기
                    </Button>

                    <div style={{ textAlign: 'center', margin: '12px 0', color: '#999' }}>또는</div>

                    <Form onSubmit={handleSubmit} className="login-form">
                        {error && <Alert variant="danger" className="login-alert">{error}</Alert>}

                        <Form.Group className="login-field" controlId="signupUsername">
                            <Form.Label>아이디 *</Form.Label>
                            <Form.Control
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                                minLength={4}
                                maxLength={30}
                                placeholder="4~30자, 영문/숫자"
                                className="login-input"
                            />
                        </Form.Group>

                        <Row>
                            <Col xs={6}>
                                <Form.Group className="login-field" controlId="signupPassword">
                                    <Form.Label>비밀번호 *</Form.Label>
                                    <Form.Control
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        minLength={8}
                                        placeholder="8자 이상"
                                        className="login-input"
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={6}>
                                <Form.Group className="login-field" controlId="signupPasswordConfirm">
                                    <Form.Label>비밀번호 확인 *</Form.Label>
                                    <Form.Control
                                        type="password"
                                        value={passwordConfirm}
                                        onChange={(e) => setPasswordConfirm(e.target.value)}
                                        required
                                        className="login-input"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="login-field" controlId="signupOwnerName">
                            <Form.Label>대표자(담당자)명 *</Form.Label>
                            <Form.Control
                                type="text"
                                value={ownerName}
                                onChange={(e) => setOwnerName(e.target.value)}
                                required
                                className="login-input"
                            />
                        </Form.Group>

                        <Form.Group className="login-field" controlId="signupEmail">
                            <Form.Label>이메일 *</Form.Label>
                            <Form.Control
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="login-input"
                            />
                        </Form.Group>

                        <Form.Group className="login-field" controlId="signupPhone">
                            <Form.Label>휴대폰 번호 *</Form.Label>
                            <Form.Control
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="010-1234-5678"
                                required
                                className="login-input"
                            />
                            <Form.Text className="text-muted">
                                결제 영수증·결제 실패 안내를 받는 번호입니다.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="login-field" controlId="signupShopName">
                            <Form.Label>매장명 *</Form.Label>
                            <Form.Control
                                type="text"
                                value={shopName}
                                onChange={(e) => setShopName(e.target.value)}
                                required
                                className="login-input"
                            />
                        </Form.Group>

                        <Form.Group className="login-field" controlId="signupBusinessNumber">
                            <Form.Label>사업자번호 *</Form.Label>
                            <Form.Control
                                type="text"
                                value={businessNumber}
                                onChange={(e) => setBusinessNumber(e.target.value)}
                                required
                                placeholder="000-00-00000"
                                className="login-input"
                            />
                        </Form.Group>

                        <Form.Group className="login-field" controlId="signupBayCount">
                            <Form.Label>세차 베이 수</Form.Label>
                            <Form.Select
                                value={bayCount}
                                onChange={(e) => setBayCount(Number(e.target.value))}
                                className="login-input"
                            >
                                <option value={1}>1개</option>
                                <option value={2}>2개</option>
                                <option value={3}>3개</option>
                                <option value={4}>4개</option>
                            </Form.Select>
                            <Form.Text className="text-muted">예약제 운영. 나중에 설정에서 변경 가능합니다.</Form.Text>
                        </Form.Group>

                        <Form.Group className="login-field" controlId="signupInviteCode">
                            <Form.Label>초대 코드 (선택)</Form.Label>
                            <Form.Control
                                type="text"
                                value={inviteCode}
                                onChange={(e) => setInviteCode(e.target.value)}
                                placeholder="사전 가입자 코드를 받으셨다면 입력"
                                className="login-input"
                            />
                        </Form.Group>

                        {requireTurnstile && (
                            <div ref={turnstileRef} style={{ marginBottom: 16 }} />
                        )}

                        <TermsAgreement
                            agreedTerms={agreedTerms}
                            agreedPrivacy={agreedPrivacy}
                            onChangeTerms={setAgreedTerms}
                            onChangePrivacy={setAgreedPrivacy}
                            disabled={stage === 'submitting'}
                        />

                        <Button
                            variant="primary"
                            type="submit"
                            className="login-submit"
                            disabled={stage === 'submitting' || !agreedAll || (requireTurnstile && !turnstileToken)}
                        >
                            {stage === 'submitting' ? '가입 처리 중...' : '30일 무료 체험 시작'}
                        </Button>
                    </Form>
                    <SiteFooter />
                </div>
            </div>
        </div>
    );
};

export default SignupPage;
